import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import { useAuth, AuthProvider } from './AuthContext';
import { getImageUrl } from './imageHelper';
import AuthScreen from './components/AuthScreen';
import { 
  Package, CheckCircle, ArrowLeft, AlertCircle, 
  Clock, Barcode, XCircle, User, LogOut,
  AlertTriangle, Check, X, Scan, Camera, Eye, EyeOff
} from 'lucide-react';
import JsBarcode from 'jsbarcode';
import { Html5QrcodeScanner } from 'html5-qrcode';

// ============================================
// WORKLIST SCREEN
// ============================================
const WorklistScreen = ({ onStartPicking, onLogout, pickerName, pickerId }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadOrders = async () => {
    setLoading(true);
    
    const { data: ordersData, error } = await supabase
      .from('orders')
      .select(`
        id,
        order_number,
        created_at,
        status,
        payment_status,
        user_id,
        picking_tasks (
          id,
          status,
          picker_id
        ),
        order_items (
          id,
          quantity,
          product_id,
          products (
            name,
            image,
            barcode
          )
        )
      `)
      .in('status', ['confirmed', 'picking'])  // Only show confirmed (paid) orders, not pending (unpaid)
      .eq('payment_status', 'paid')  // Only show paid orders
      .order('created_at', { ascending: true });

    if (!error && ordersData) {
      const availableOrders = ordersData.filter(order => {
        const pickingTask = order.picking_tasks?.[0];
        
        // No picking task yet - available for picking
        if (!pickingTask) return true;
        
        // Picking task is pending - available for picking
        if (pickingTask.status === 'pending') return true;
        
        // Picking task is in progress by this picker - show so they can continue
        if (pickingTask.status === 'in_progress' && pickingTask.picker_id === pickerId) return true;
        
        // Exclude orders with completed, not_picked, or any other status
        // This catches orders that were picked/delivered but order status wasn't updated
        return false;
      });
      
      setOrders(availableOrders);
      
      const myInProgressOrder = availableOrders.find(order => {
        const pickingTask = order.picking_tasks?.[0];
        return pickingTask?.status === 'in_progress' && pickingTask?.picker_id === pickerId;
      });
      
      if (myInProgressOrder) {
        onStartPicking(myInProgressOrder);
      }
    }
    
    setLoading(false);
  };

  useEffect(() => {
    loadOrders();
    const interval = setInterval(loadOrders, 30000);
    return () => clearInterval(interval);
  }, []);

  const getTotalItems = (order) => {
    return order.order_items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-green-100 p-2 rounded-lg">
                <Package size={24} className="text-green-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Picking Worklist</h1>
                <p className="text-sm text-gray-600">{orders.length} orders available</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="text-right mr-2">
                <p className="text-xs text-gray-600">Picker</p>
                <p className="text-sm font-semibold text-gray-900">{pickerName}</p>
              </div>
              <button
                onClick={onLogout}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Logout"
              >
                <LogOut size={20} className="text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="p-4">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading orders...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-gray-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package size={40} className="text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Orders Available</h3>
            <p className="text-gray-600">Check back soon for new orders to pick</p>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map(order => {
              const pickingTask = order.picking_tasks?.[0];
              const isInProgress = pickingTask?.status === 'in_progress' && pickingTask?.picker_id === pickerId;
              
              return (
                <button
                  key={order.id}
                  onClick={() => onStartPicking(order)}
                  className={`w-full bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow border-2 text-left ${
                    isInProgress ? 'border-orange-400 bg-orange-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-1">
                        Order #{order.order_number}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {new Date(order.created_at).toLocaleString()}
                      </p>
                    </div>
                    {isInProgress && (
                      <span className="px-3 py-1 bg-orange-500 text-white text-xs font-bold rounded-full">
                        IN PROGRESS
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-gray-700">
                      <Package size={18} />
                      <span className="text-sm font-medium">
                        {getTotalItems(order)} items
                      </span>
                    </div>
                    
                    <div className="text-green-600 font-semibold text-sm">
                      {isInProgress ? 'Continue →' : 'Start Picking →'}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

// ============================================
// PICKING SCREEN - WITH DUAL SCANNING SUPPORT
// ============================================
const PickingScreen = ({ order, onComplete, onCancel, pickerId }) => {
  const [itemStatuses, setItemStatuses] = useState({});
  const [scannedBarcode, setScannedBarcode] = useState('');
  const [error, setError] = useState('');
  const [pickingTaskId, setPickingTaskId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showShortageModal, setShowShortageModal] = useState(false);
  const [shortageItemId, setShortageItemId] = useState(null);
  const [shortageReason, setShortageReason] = useState('');
  const [isSubmittingShortage, setIsSubmittingShortage] = useState(false);
  const [partialQuantity, setPartialQuantity] = useState('');
  
  const [showCameraScanner, setShowCameraScanner] = useState(false);
  const scannerInstanceRef = useRef(null);
  const barcodeInputRef = useRef(null);

  useEffect(() => {
    const initStatuses = {};
    order.order_items.forEach(item => {
      initStatuses[item.id] = {
        ...item,
        pickedQuantity: 0,
        status: 'pending'
      };
    });
    setItemStatuses(initStatuses);
    initializePickingTask();
  }, [order]);

  useEffect(() => {
    return () => {
      if (scannerInstanceRef.current) {
        scannerInstanceRef.current.clear().catch(err => console.error('Error clearing scanner:', err));
      }
    };
  }, []);

  useEffect(() => {
    const keepFocused = () => {
      if (!showCameraScanner && !showShortageModal && barcodeInputRef.current) {
        barcodeInputRef.current.focus();
      }
    };

    keepFocused();
    const interval = setInterval(keepFocused, 500);
    const handleClick = (e) => {
      // Don't refocus if clicking inside the shortage modal
      if (showShortageModal) return;
      setTimeout(keepFocused, 100);
    };
    
    document.addEventListener('click', handleClick);

    return () => {
      clearInterval(interval);
      document.removeEventListener('click', handleClick);
    };
  }, [showCameraScanner, showShortageModal]);

  useEffect(() => {
    if (showCameraScanner && !scannerInstanceRef.current) {
      const timer = setTimeout(() => {
        initializeCameraScanner();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showCameraScanner]);

  const initializePickingTask = async () => {
    let taskId = order.picking_tasks?.[0]?.id;
    
    if (!taskId) {
      const { data, error } = await supabase
        .from('picking_tasks')
        .insert({
          order_id: order.id,
          picker_id: pickerId,
          status: 'in_progress',
          started_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (!error) {
        taskId = data.id;
        await supabase
          .from('orders')
          .update({ status: 'picking' })
          .eq('id', order.id);
      }
    } else {
      await supabase
        .from('picking_tasks')
        .update({
          status: 'in_progress',
          picker_id: pickerId,
          started_at: new Date().toISOString()
        })
        .eq('id', taskId);
      
      await supabase
        .from('orders')
        .update({ status: 'picking' })
        .eq('id', order.id);
    }
    
    setPickingTaskId(taskId);
  };

  const initializeCameraScanner = () => {
    try {
      const scanner = new Html5QrcodeScanner(
        "barcode-reader",
        { 
          fps: 10,
          qrbox: { width: 400, height: 300 },
          aspectRatio: 1.333,
          rememberLastUsedCamera: true,
          formatsToSupport: [0, 8, 13, 14, 16, 17, 7, 9, 12]
        },
        false
      );

      scanner.render(onCameraScanSuccess, onCameraScanFailure);
      scannerInstanceRef.current = scanner;
    } catch (error) {
      console.error('Error initializing camera scanner:', error);
      setError('Failed to initialize camera. Please check camera permissions.');
      setShowCameraScanner(false);
    }
  };

  const onCameraScanSuccess = (decodedText, decodedResult) => {
    console.log('Camera scan successful:', decodedText);
    
    if (scannerInstanceRef.current) {
      scannerInstanceRef.current.clear().catch(err => console.error('Error clearing scanner:', err));
      scannerInstanceRef.current = null;
    }
    
    setShowCameraScanner(false);
    setScannedBarcode(decodedText);
    processBarcode(decodedText);
  };

  const onCameraScanFailure = (error) => {
    // Ignore scan failures
  };

  const findItemByBarcode = (barcode) => {
    const normalizedBarcode = barcode.trim();
    let foundButCompleted = false;
    
    for (const [itemId, itemStatus] of Object.entries(itemStatuses)) {
      const productBarcode = itemStatus.products?.barcode?.trim() || '';
      
      if (productBarcode === normalizedBarcode) {
        if (itemStatus.pickedQuantity < itemStatus.quantity) {
          return { itemId, itemStatus, status: 'needsPicking' };
        } else {
          foundButCompleted = true;
        }
      }
    }
    
    return foundButCompleted ? { status: 'alreadyPicked' } : null;
  };

  const processBarcode = (barcode) => {
    setError('');

    if (!barcode.trim()) {
      setError('Please scan or enter a barcode');
      return;
    }

    const match = findItemByBarcode(barcode);
    
    if (!match) {
      const unpickedItems = Object.entries(itemStatuses).filter(([_, item]) => 
        item.pickedQuantity < item.quantity && 
        item.status !== 'shortage' && 
        item.status !== 'partial'
      );
      
      if (unpickedItems.length > 0) {
        setError(`Barcode "${barcode}" not found in this order. Please verify the item or use "Report Shortage" for incorrect items.`);
      } else {
        setError(`All items have been picked. Barcode: "${barcode}"`);
      }
      
      setScannedBarcode('');
      return;
    }

    if (match.status === 'alreadyPicked') {
      setError(`✅ This item has already been picked!`);
      setScannedBarcode('');
      return;
    }

    const { itemId, itemStatus } = match;
    const newQty = itemStatus.pickedQuantity + 1;

    setItemStatuses(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        pickedQuantity: prev[itemId].pickedQuantity + 1,
        status: prev[itemId].pickedQuantity + 1 >= prev[itemId].quantity ? 'picked' : 'pending'
      }
    }));

    // Persist each scan so the picking dashboard can show live progress.
    // Fire-and-forget: a failed write must never block the picker mid-aisle —
    // handleComplete rewrites every quantity authoritatively at the end.
    supabase
      .from('order_items')
      .update({ picked_quantity: newQty })
      .eq('id', itemId)
      .then(({ error }) => { if (error) console.error('progress write failed:', error); });

    setScannedBarcode('');
    
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  };

  const handleBarcodeSubmit = (e) => {
    e.preventDefault();
    processBarcode(scannedBarcode);
  };

  const handleReportShortage = (itemId) => {
    setShortageItemId(itemId);
    setPartialQuantity(''); // Reset to empty - default will be 0
    setShortageReason(''); // Reset reason too
    setShowShortageModal(true);
  };

  const handleShortageSubmit = async () => {
    if (!shortageReason || shortageItemId === null) return;
    if (isSubmittingShortage) return; // guard against double-click duplicate submits
    setIsSubmittingShortage(true);
  
    const item = itemStatuses[shortageItemId];
    
    // If out_of_stock, picked quantity must be 0
    // Otherwise use the entered partial quantity
    let pickedQty = 0;
    if (shortageReason === 'out_of_stock') {
      pickedQty = 0; // Force 0 for out of stock
    } else if (partialQuantity && partialQuantity.trim() !== '') {
      pickedQty = parseInt(partialQuantity);
      // Ensure it's within valid range
      if (isNaN(pickedQty) || pickedQty < 0) {
        pickedQty = 0;
      }
      if (pickedQty >= item.quantity) {
        // Can't pick full quantity and report shortage
        alert('Partial quantity must be less than ordered quantity');
        setIsSubmittingShortage(false);
        return;
      }
    }
  
    setItemStatuses(prev => ({
      ...prev,
      [shortageItemId]: {
        ...prev[shortageItemId],
        pickedQuantity: pickedQty,
        status: pickedQty === 0 ? 'shortage' : 'partial',
        shortageReason: shortageReason
      }
    }));
  
    // Insert shortage record
    await supabase.from('picking_shortages').insert({
      picking_task_id: pickingTaskId,
      order_item_id: shortageItemId,
      expected_quantity: item.quantity,
      picked_quantity: pickedQty,
      reason: shortageReason
    });

    // UPDATE order_items.picked_quantity to reflect what was actually picked
    await supabase
      .from('order_items')
      .update({ picked_quantity: pickedQty })
      .eq('id', shortageItemId);

    // Wallet credit for shortages is issued by the DATABASE when the order reaches
    // 'picking_complete' (trigger: trg_shortage_credits). The picker records what was
    // picked; it never moves money — client-side code cannot be trusted to complete a
    // financial transaction if the app is closed, loses signal, or the flow is reset.

    setShowShortageModal(false);
    setShortageItemId(null);
    setShortageReason('');
    setPartialQuantity('');
    setIsSubmittingShortage(false);
  };

  const allItemsCompleted = () => {
    return Object.values(itemStatuses).every(item => 
      item.status === 'picked' || item.status === 'partial' || item.status === 'shortage'
    );
  };

  const handleComplete = async () => {
    // PREVENT DOUBLE-CLICK - if already submitting, ignore
    if (isSubmitting) {
      console.log('Already submitting, ignoring duplicate click');
      return;
    }

    if (!allItemsCompleted()) {
      setError('Please complete all items before finishing');
      return;
    }

    setIsSubmitting(true); // Disable button immediately

    try {
      const totalPickedQuantity = Object.values(itemStatuses).reduce(
        (sum, item) => sum + item.pickedQuantity, 
        0
      );

      if (totalPickedQuantity === 0) {
        await supabase
          .from('picking_tasks')
          .update({
            status: 'not_picked',
            completed_at: new Date().toISOString()
          })
          .eq('id', pickingTaskId);

        await supabase
          .from('orders')
          .update({ status: 'stock_unavailable' })
          .eq('id', order.id);

        const shortageCount = Object.values(itemStatuses).filter(
          item => item.status === 'shortage' || item.status === 'partial'
        ).length;
        
        const totalItems = Object.values(itemStatuses).reduce(
          (sum, item) => sum + item.quantity, 
          0
        );

        onComplete('N/A', {
          order_number: order.order_number,
          items: totalItems,
          shortages: shortageCount,
          fullShortage: true
        });
        return;
      }

      const { data: pickNumberData, error: pickNumberError } = await supabase
        .rpc('get_next_pick_number');
      
      if (pickNumberError) {
        console.error('Error generating pick number:', pickNumberError);
        setError('Failed to generate pick number. Please try again.');
        setIsSubmitting(false); // Re-enable button on error
        return;
      }
      
      const pickNumber = pickNumberData;

      // Calculate shortage information
      const shortageItems = Object.entries(itemStatuses).filter(
        ([, item]) => item.status === 'partial' || item.status === 'shortage'
      );
      
      const hasShortages = shortageItems.length > 0;
      
      let totalShortageValue = 0;
      const shortageDetails = shortageItems.map(([itemId, item]) => {
        const orderItem = order.order_items.find(oi => oi.id === itemId);
        const shortageQty = item.quantity - item.pickedQuantity;
        const unitPrice = parseFloat(orderItem?.price || orderItem?.unit_price || 0);
        const shortageValue = shortageQty * unitPrice;
        totalShortageValue += shortageValue;
        
        return {
          order_item_id: itemId,
          product_id: orderItem?.product_id,
          name: orderItem?.products?.name || 'Unknown',
          ordered: item.quantity,
          picked: item.pickedQuantity,
          shortage: shortageQty,
          unit_price: unitPrice,
          value: shortageValue,
          reason: item.shortageReason || 'not_specified'
        };
      });

      await supabase
        .from('picking_tasks')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          pick_number: pickNumber,
          has_shortages: hasShortages,
          shortage_value: totalShortageValue,
          shortage_details: shortageDetails
        })
        .eq('id', pickingTaskId);

      await supabase
        .from('orders')
        .update({ 
          status: 'picked',
          pick_number: pickNumber
        })
        .eq('id', order.id);

      // Update order_items.picked_quantity
      //
      // AN UNSCANNED ITEM IS NOT A ZERO PICK. pickedQuantity only ever counts
      // BARCODE SCANS — it starts at 0 and increments on each scan. Writing it
      // straight through meant any line the picker did not scan (no barcode, a
      // damaged label, picked by eye) was recorded as "0 delivered". The
      // customer then saw a paid, delivered order in which nothing arrived and
      // everything cost R0.00.
      //
      // A shortage in this system is DECLARED, through the shortage modal, which
      // sets status to 'shortage' or 'partial' and records the true quantity —
      // and that declaration is what the wallet-credit trigger acts on. So a
      // line with no declaration was picked in full; only a declared shortage
      // writes less than what was ordered.
      for (const [itemId, itemStatus] of Object.entries(itemStatuses)) {
        const declaredShort = itemStatus.status === 'shortage' || itemStatus.status === 'partial';
        const qty = declaredShort
          ? itemStatus.pickedQuantity
          : (itemStatus.quantity ?? itemStatus.pickedQuantity);
        await supabase
          .from('order_items')
          .update({ picked_quantity: qty })
          .eq('id', itemId);
      }

      // UPDATE INVENTORY WITH DUPLICATE PREVENTION
      try {
        const { data: warehouse } = await supabase
          .from('warehouses')
          .select('id')
          .eq('code', 'WH001')
          .single();

        const { data: location } = await supabase
          .from('storage_locations')
          .select('id')
          .eq('warehouse_id', warehouse?.id)
          .eq('code', 'FG001')
          .single();

        if (warehouse && location) {
          for (const [itemId, itemStatus] of Object.entries(itemStatuses)) {
            const pickedQty = itemStatus.pickedQuantity;
            
            if (pickedQty > 0) {
              // FIX: Check if issue movement already exists for this order/product
              const { data: existingMovement } = await supabase
                .from('stock_movements')
                .select('id')
                .eq('movement_type', 'issue')
                .eq('product_id', itemStatus.product_id)
                .eq('warehouse_id', warehouse.id)
                .eq('location_id', location.id)
                .ilike('notes', `%${order.order_number}%`)
                .maybeSingle();

              // Only create movement if one doesn't already exist
              if (!existingMovement) {
                const { data: currentPosition } = await supabase
                  .from('inventory_positions')
                  .select('quantity')
                  .eq('product_id', itemStatus.product_id)
                  .eq('warehouse_id', warehouse.id)
                  .eq('location_id', location.id)
                  .single();

                if (currentPosition) {
                  const newQuantity = Math.max(0, currentPosition.quantity - pickedQty);
                  
                  await supabase
                    .from('inventory_positions')
                    .update({ 
                      quantity: newQuantity,
                      updated_at: new Date().toISOString()
                    })
                    .eq('product_id', itemStatus.product_id)
                    .eq('warehouse_id', warehouse.id)
                    .eq('location_id', location.id);

                  await supabase
                    .from('stock_movements')
                    .insert({
                      product_id: itemStatus.product_id,
                      warehouse_id: warehouse.id,
                      location_id: location.id,
                      movement_type: 'issue',
                      quantity: pickedQty,
                      previous_stock: currentPosition.quantity,
                      new_stock: newQuantity,
                      notes: `Order #${order.order_number} picked - Pick #${pickNumber}`,
                      created_by: pickerId
                    });
                }
              } else {
                console.log(`Skipping duplicate issue for order ${order.order_number}, product ${itemStatus.product_id}`);
              }
            }
          }
        }
      } catch (inventoryError) {
        console.error('Error updating inventory:', inventoryError);
      }

      const shortages = Object.values(itemStatuses).filter(
        item => item.status === 'partial' || item.status === 'shortage'
      ).length;

      const qrData = {
        order_id: order.id,
        order_number: order.order_number,
        pick_number: pickNumber,
        items: order.order_items.length,
        shortages: shortages,
        timestamp: new Date().toISOString()
      };

      onComplete(pickNumber, qrData);
    } catch (error) {
      console.error('Error completing pick:', error);
      setError('Failed to complete. Please try again.');
      setIsSubmitting(false); // Re-enable button on error
    }
  };

  const totalItems = order.order_items.reduce((sum, item) => sum + item.quantity, 0);
  const pickedItems = Object.values(itemStatuses).reduce((sum, item) => sum + item.pickedQuantity, 0);
  const progress = totalItems > 0 ? (pickedItems / totalItems) * 100 : 0;

  const sortedItems = Object.entries(itemStatuses).sort(([, a], [, b]) => {
    const aComplete = a.status === 'picked' || a.status === 'partial' || a.status === 'shortage';
    const bComplete = b.status === 'picked' || b.status === 'partial' || b.status === 'shortage';
    if (aComplete && !bComplete) return 1;
    if (!aComplete && bComplete) return -1;
    return 0;
  });

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* TEST BANNER - Remove after confirming update works */}
      <div className="bg-red-500 text-white p-1 text-center text-xs font-bold">v2.0 DT50</div>
      
      {/* Compact Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="px-3 py-2">
          <div className="flex items-center justify-between">
            <button
              onClick={onCancel}
              className="flex items-center gap-1 text-gray-600"
            >
              <ArrowLeft size={18} />
              <span className="text-sm font-medium">Back</span>
            </button>
            
            <div className="text-right">
              <h2 className="text-sm font-bold text-gray-900">
                Order #{order.order_number}
              </h2>
              <p className="text-xs text-gray-500">
                {pickedItems} / {totalItems} items
              </p>
            </div>
          </div>

          <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
            <div
              className="bg-green-600 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </header>

      {/* Scanner Area */}
      <div className="bg-white border-b border-gray-200 p-2 sticky top-[60px] z-10">
        {!showCameraScanner ? (
          <form onSubmit={handleBarcodeSubmit}>
            <div className="flex gap-1.5">
              <div className="flex-1 relative">
                <Barcode className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  ref={barcodeInputRef}
                  type="text"
                  value={scannedBarcode}
                  onChange={(e) => setScannedBarcode(e.target.value)}
                  placeholder="Scan or enter barcode..."
                  className="w-full pl-8 pr-2 py-2 border-2 border-green-500 rounded-lg bg-green-50 text-sm"
                  autoFocus
                  autoComplete="off"
                />
              </div>
              <button
                type="submit"
                className="px-3 py-2 bg-green-600 text-white rounded-lg"
              >
                <Scan size={18} />
              </button>
              <button
                type="button"
                onClick={() => setShowCameraScanner(true)}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg"
              >
                <Camera size={18} />
              </button>
            </div>

            <div className="flex items-center justify-center gap-1 text-xs text-green-600 mt-1.5">
              <Scan size={12} />
              <span>Scanner Ready - Point and Scan</span>
            </div>

            {error && (
              <div className="flex items-start gap-2 p-2 mt-2 bg-red-50 border border-red-200 rounded-lg text-red-700">
                <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                <span className="text-xs">{error}</span>
              </div>
            )}
          </form>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold text-gray-900">Camera Scanner</h3>
              <button
                onClick={() => {
                  if (scannerInstanceRef.current) {
                    scannerInstanceRef.current.clear().catch(err => console.error('Error clearing scanner:', err));
                    scannerInstanceRef.current = null;
                  }
                  setShowCameraScanner(false);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} className="text-gray-600" />
              </button>
            </div>
            <div className="bg-gray-100 rounded-lg p-2">
              <div id="barcode-reader" className="rounded-lg overflow-hidden min-h-[400px]"></div>
            </div>
            <p className="text-sm text-gray-600 text-center">
              Position barcode within the frame
            </p>
          </div>
        )}
      </div>

      <main className="p-2 space-y-2" onMouseDown={(e) => {
        if (!e.target.closest('button')) {
          e.preventDefault();
        }
      }}>
        {sortedItems.map(([itemId, itemStatus]) => {
          const isComplete = itemStatus.status === 'picked' || itemStatus.status === 'partial' || itemStatus.status === 'shortage';
          const needsMore = itemStatus.pickedQuantity < itemStatus.quantity && itemStatus.status !== 'shortage' && itemStatus.status !== 'partial';
          
          return (
            <div
              key={itemId}
              className={`bg-white rounded-xl p-3 shadow-sm border-2 ${
                isComplete ? 'border-green-400 bg-green-50' : 
                itemStatus.pickedQuantity > 0 ? 'border-blue-400 bg-blue-50' : 
                'border-gray-200'
              }`}
            >
              {/* Product info row */}
              <div className="flex gap-3 items-start">
                <div className="w-14 h-14 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                  {itemStatus.products?.image ? (
                    <img
                      src={getImageUrl(itemStatus.products.image)}
                      alt={itemStatus.products?.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package size={20} className="text-gray-400" />
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0 overflow-hidden">
                  <h3 className="font-semibold text-gray-900 text-sm leading-tight break-words">
                    {itemStatus.products?.name || 'Product'}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">
                    {itemStatus.products?.barcode || 'N/A'}
                  </p>
                </div>
              </div>
              
              {/* Quantity and status row */}
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                <div className={`text-xl font-bold ${
                  isComplete ? 'text-green-600' :
                  itemStatus.pickedQuantity > 0 ? 'text-blue-600' :
                  'text-gray-900'
                }`}>
                  {itemStatus.pickedQuantity} / {itemStatus.quantity}
                </div>
                
                {isComplete && (
                  <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                    itemStatus.status === 'picked' ? 'bg-green-100 text-green-700' :
                    itemStatus.status === 'partial' ? 'bg-yellow-100 text-yellow-700' : 
                    'bg-red-100 text-red-700'
                  }`}>
                    {itemStatus.status === 'picked' ? '✓ Done' :
                     itemStatus.status === 'partial' ? 'Partial' : 'Shortage'}
                  </span>
                )}
                
                {needsMore && itemStatus.pickedQuantity > 0 && (
                  <span className="text-xs font-bold px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                    In Progress
                  </span>
                )}
              </div>

              {itemStatus.shortageReason && (
                <p className="text-xs text-orange-600 mt-1">
                  Reason: {itemStatus.shortageReason.replace('_', ' ')}
                </p>
              )}

              {/* Report Shortage button - FULL WIDTH BELOW */}
              {!isComplete && (
                <button
                  onClick={() => handleReportShortage(itemId)}
                  className="w-full mt-3 py-2.5 border-2 border-orange-500 text-orange-600 font-bold text-sm rounded-lg active:bg-orange-100"
                >
                  Report Shortage
                </button>
              )}
            </div>
          );
        })}
      </main>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-2 shadow-lg">
        <button
          onClick={handleComplete}
          disabled={!allItemsCompleted() || isSubmitting}
          className={`w-full py-3 rounded-lg font-bold text-white text-sm ${
            allItemsCompleted() && !isSubmitting
              ? 'bg-green-600 active:bg-green-700'
              : 'bg-gray-400'
          }`}
        >
          {isSubmitting 
            ? 'Processing...' 
            : allItemsCompleted() 
              ? 'Complete Order' 
              : 'Pick All Items to Continue'}
        </button>
      </div>

      {showShortageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-orange-100 p-2 rounded-lg">
                <AlertTriangle size={24} className="text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Report Shortage</h3>
            </div>

            <div className="space-y-4 mb-6">
              {/* Only show partial quantity input if not out_of_stock */}
              {shortageReason !== 'out_of_stock' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Picked Quantity (how many were you able to pick?)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={partialQuantity}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, '');
                      setPartialQuantity(val);
                    }}
                    onBlur={(e) => {
                      const maxQty = (itemStatuses[shortageItemId]?.quantity || 1) - 1;
                      const val = parseInt(partialQuantity) || 0;
                      if (val > maxQty) {
                        setPartialQuantity(String(maxQty));
                      }
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-lg"
                    placeholder="0"
                    autoComplete="off"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Max: {(itemStatuses[shortageItemId]?.quantity || 1) - 1}
                  </p>
                </div>
              )}

              {shortageReason === 'out_of_stock' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-700 text-sm font-medium">Picked quantity will be set to 0 (none available)</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason
                </label>
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShortageReason('out_of_stock');
                      setPartialQuantity(''); // Clear - will default to 0
                    }}
                    className={`w-full px-4 py-3 border-2 rounded-lg text-left font-semibold transition-all ${
                      shortageReason === 'out_of_stock'
                        ? 'border-orange-500 bg-orange-50 text-orange-900'
                        : 'border-gray-300 text-gray-700 hover:border-orange-300'
                    }`}
                  >
                    Out of Stock
                  </button>
                  <button
                    type="button"
                    onClick={() => setShortageReason('damaged')}
                    className={`w-full px-4 py-3 border-2 rounded-lg text-left font-semibold transition-all ${
                      shortageReason === 'damaged'
                        ? 'border-orange-500 bg-orange-50 text-orange-900'
                        : 'border-gray-300 text-gray-700 hover:border-orange-300'
                    }`}
                  >
                    Damaged
                  </button>
                  <button
                    type="button"
                    onClick={() => setShortageReason('insufficient_stock')}
                    className={`w-full px-4 py-3 border-2 rounded-lg text-left font-semibold transition-all ${
                      shortageReason === 'insufficient_stock'
                        ? 'border-orange-500 bg-orange-50 text-orange-900'
                        : 'border-gray-300 text-gray-700 hover:border-orange-300'
                    }`}
                  >
                    Insufficient Stock
                  </button>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowShortageModal(false);
                  setShortageReason('');
                  setPartialQuantity('');
                }}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleShortageSubmit}
                disabled={!shortageReason || isSubmittingShortage}
                className="flex-1 px-6 py-3 bg-orange-600 text-white font-bold rounded-lg hover:bg-orange-700 transition-colors disabled:bg-gray-400"
              >
                {isSubmittingShortage ? 'Processing…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// COMPLETION SCREEN
// ============================================
const CompletionScreen = ({ pickNumber, qrData, onContinue }) => {
  const barcodeRef = useRef(null);
  const isFullShortage = qrData.fullShortage === true;

  useEffect(() => {
    if (barcodeRef.current && pickNumber && pickNumber !== 'N/A') {
      try {
        JsBarcode(barcodeRef.current, String(pickNumber), {
          format: 'CODE128',
          width: 3,
          height: 120,
          displayValue: true,
          fontSize: 24,
          margin: 15,
          background: '#ffffff',
          lineColor: '#000000'
        });
      } catch (err) {
        console.error('Error generating barcode:', err);
      }
    }
  }, [pickNumber]);

  // Full shortage screen - different UI
  if (isFullShortage) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="bg-orange-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertTriangle size={40} className="text-orange-600" />
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Stock Unavailable
          </h1>
          <p className="text-gray-600 mb-8">
            Order #{qrData.order_number}
          </p>

          <div className="bg-orange-50 rounded-xl p-6 mb-6 border-2 border-orange-200">
            <p className="text-sm text-orange-800 font-medium mb-2">
              All items reported as shortage
            </p>
            <p className="text-sm text-gray-600">
              This order has been removed from the picking queue. Customer refund credits have been queued for admin approval.
            </p>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
            <div className="flex justify-between mb-2">
              <span className="text-sm text-gray-600">Total Items:</span>
              <span className="text-sm font-semibold text-gray-900">
                {qrData.items}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Shortages:</span>
              <span className="text-sm font-semibold text-orange-600">
                {qrData.shortages} (100%)
              </span>
            </div>
          </div>

          <button
            onClick={onContinue}
            className="w-full bg-orange-600 text-white font-bold py-4 rounded-lg hover:bg-orange-700 transition-colors"
          >
            Continue to Next Order
          </button>
        </div>
      </div>
    );
  }

  // Normal completion screen
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle size={40} className="text-green-600" />
        </div>
        
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Picking Complete!
        </h1>
        <p className="text-gray-600 mb-8">
          Order #{qrData.order_number}
        </p>

        <div className="bg-blue-50 rounded-xl p-6 mb-6">
          <p className="text-sm text-gray-600 mb-2">Pick Number</p>
          <p className="text-6xl font-bold text-blue-600 mb-2">{pickNumber}</p>
          <p className="text-sm text-gray-600">Attach this label to the order</p>
        </div>

        <div className="bg-white rounded-xl p-6 mb-6 border-2 border-blue-300 shadow-sm">
          <p className="text-sm font-bold text-blue-700 mb-3 text-center">SCAN WITH HANDHELD SCANNER</p>
          <div className="bg-white p-4 rounded-lg">
            <svg ref={barcodeRef} className="mx-auto"></svg>
          </div>
          <p className="text-xs text-gray-600 mt-3 text-center font-medium">CODE-128 Barcode for Packing</p>
        </div>

        <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
          <div className="flex justify-between mb-2">
            <span className="text-sm text-gray-600">Total Items:</span>
            <span className="text-sm font-semibold text-gray-900">
              {qrData.items}
            </span>
          </div>
          {qrData.shortages > 0 && (
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Shortages/Partial:</span>
              <span className="text-sm font-semibold text-orange-600">
                {qrData.shortages}
              </span>
            </div>
          )}
        </div>

        <button
          onClick={onContinue}
          className="w-full bg-green-600 text-white font-bold py-4 rounded-lg hover:bg-green-700 transition-colors"
        >
          Continue to Next Order
        </button>
      </div>
    </div>
  );
};

// ============================================
// MAIN PICKER APP
// ============================================
const PickerApp = () => {
  const { user, userProfile, signOut } = useAuth();
  const [currentScreen, setCurrentScreen] = useState('worklist');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [completionData, setCompletionData] = useState(null);

  const handleStartPicking = (order) => {
    setSelectedOrder(order);
    setCurrentScreen('picking');
  };

  const handleCompletePicking = (pickNumber, qrData) => {
    setCompletionData({ pickNumber, qrData });
    setCurrentScreen('completion');
  };

  const handleContinue = () => {
    setSelectedOrder(null);
    setCompletionData(null);
    setCurrentScreen('worklist');
  };

  // Statuses that mean the order has moved BEYOND picking. Cancelling out of the
  // picker must never drag these back to 'confirmed' — doing so hides a finished
  // order from the packer/driver.
  const POST_PICKING_STATUSES = ['picking_complete', 'packing', 'packed', 'invoice_sent', 'ready_for_dispatch', 'out_for_delivery', 'delivered', 'completed', 'cancelled'];

  const handleCancelPicking = async () => {
    if (selectedOrder) {
      // Re-read the CURRENT status from the DB (selectedOrder may be stale)
      const { data: fresh } = await supabase
        .from('orders')
        .select('status')
        .eq('id', selectedOrder.id)
        .single();

      const currentStatus = fresh?.status || selectedOrder.status;

      if (POST_PICKING_STATUSES.includes(currentStatus)) {
        console.log(`⛔ Not resetting order — already at '${currentStatus}' (past picking)`);
        alert(`This order has already moved on (${currentStatus}). It has not been changed.`);
      } else {
        await supabase
          .from('orders')
          .update({ status: 'confirmed' })
          .eq('id', selectedOrder.id)
          .in('status', ['picking', 'confirmed', 'pending']);   // DB-side guard too

        await supabase
          .from('picking_tasks')
          .update({ 
            status: 'pending',
            picker_id: null,
            started_at: null
          })
          .eq('order_id', selectedOrder.id);
      }
    }
    
    setSelectedOrder(null);
    setCurrentScreen('worklist');
  };

  const handleLogout = async () => {
    await signOut();
  };

  const pickerName = userProfile?.name || user.email?.split('@')[0] || 'Picker';

  switch (currentScreen) {
    case 'picking':
      return (
        <PickingScreen
          order={selectedOrder}
          onComplete={handleCompletePicking}
          onCancel={handleCancelPicking}
          pickerId={user.id}
        />
      );

    case 'completion':
      return (
        <CompletionScreen
          pickNumber={completionData.pickNumber}
          qrData={completionData.qrData}
          onContinue={handleContinue}
        />
      );

    default:
      return (
        <WorklistScreen
          onStartPicking={handleStartPicking}
          onLogout={handleLogout}
          pickerName={pickerName}
          pickerId={user.id}
        />
      );
  }
};

// ============================================
// APP WRAPPER WITH AUTH
// ============================================
function AppContent() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <AuthScreen />;
  }

  return <PickerApp />;
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
